import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Droplets, Sun, Moon, Sparkles, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SkincareGuide() {
  const navigate = useNavigate();

  const morningRoutine = [
    {
      step: 1,
      product: "Gentle Cleanser",
      why: "Removes overnight oil and prepares skin for products",
      tip: "Look for: Ceramides, hyaluronic acid, or gentle surfactants. Avoid: Sulfates, alcohol, heavy fragrances.",
      duration: "60 seconds"
    },
    {
      step: 2,
      product: "Vitamin C Serum",
      why: "Brightens, protects from free radicals, boosts collagen",
      tip: "Apply to slightly damp skin. Start with 10-15% concentration. Store in dark place.",
      duration: "Let absorb 1-2 min"
    },
    {
      step: 3,
      product: "Hydrating Moisturizer",
      why: "Locks in hydration and creates protective barrier",
      tip: "Look for: Hyaluronic acid, peptides, niacinamide. Apply while serum is still slightly tacky.",
      duration: "30 seconds"
    },
    {
      step: 4,
      product: "SPF 30+ Sunscreen",
      why: "Prevents 90% of visible aging signs—this is NON-NEGOTIABLE",
      tip: "Use 2 finger-lengths for face/neck. Reapply every 2 hours if outdoors. Mineral or chemical both work.",
      duration: "30 seconds"
    }
  ];

  const eveningRoutine = [
    {
      step: 1,
      product: "Oil Cleanser / Micellar Water",
      why: "Dissolves makeup, sunscreen, and daily grime",
      tip: "Massage onto dry skin for 60 seconds before adding water. Double cleanse if wearing heavy makeup.",
      duration: "60-90 seconds"
    },
    {
      step: 2,
      product: "Gentle Cleanser",
      why: "Second cleanse removes any remaining residue",
      tip: "Same as morning—gentle, pH-balanced, non-stripping.",
      duration: "60 seconds"
    },
    {
      step: 3,
      product: "Treatment (Retinol or Acids)",
      why: "Active ingredients that promote cell turnover and collagen",
      tip: "Start retinol 2x/week, work up to nightly. Don't mix retinol with AHAs/BHAs. Alternate nights.",
      duration: "Let absorb 5-10 min"
    },
    {
      step: 4,
      product: "Rich Night Cream or Facial Oil",
      why: "Deeper hydration and repair while you sleep",
      tip: "Look for: Peptides, retinol (if not using separate), ceramides, squalane. Skin repairs at night—give it fuel.",
      duration: "30 seconds"
    },
    {
      step: 5,
      product: "Eye Cream",
      why: "Delicate eye area needs targeted care",
      tip: "Use ring finger, dab gently (don't rub). Look for: Caffeine (puffiness), peptides (firmness), vitamin K (dark circles).",
      duration: "30 seconds"
    }
  ];

  const keyIngredients = [
    { name: "Retinol (Vitamin A)", benefit: "Gold standard for anti-aging. Boosts collagen, reduces wrinkles, evens tone.", caution: "Start slow, always use SPF" },
    { name: "Vitamin C", benefit: "Antioxidant protection, brightening, collagen support", caution: "Can oxidize—store properly" },
    { name: "Hyaluronic Acid", benefit: "Holds 1000x its weight in water. Plumps and hydrates.", caution: "Apply to damp skin" },
    { name: "Niacinamide (B3)", benefit: "Minimizes pores, evens skin tone, strengthens barrier", caution: "Can be used with most ingredients" },
    { name: "Peptides", benefit: "Signal skin to produce more collagen and elastin", caution: "Gentle, good for sensitive skin" },
    { name: "AHAs/BHAs", benefit: "Exfoliate dead skin, unclog pores, improve texture", caution: "Don't overdo it—2-3x/week max" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-4">
            Week 3 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Skincare in Your 40s
          </h1>
          <p className="text-xl text-secondary-600">
            A Complete Morning & Evening Routine for Radiant Skin
          </p>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/e7861f24023d7cb2724ca1dec035bfdf1919ad3f9519b9c6946ed6ad62965fb1.png"
            alt="Skincare Routine"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Why Your 40s Skin Needs Different Care
          </h2>
          <p className="text-secondary-700 mb-4">
            In your 40s, your skin is going through real changes: collagen production slows by about 1% per year, cell turnover decreases, and estrogen fluctuations can affect moisture levels. The good news? A targeted routine can address all of this.
          </p>
          <p className="text-secondary-700 mb-4">
            This isn't about fighting aging—it's about supporting your skin's natural processes and giving it what it needs to look healthy and radiant at every age.
          </p>
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
            <p className="text-secondary-800 font-medium flex items-start gap-2">
              <Heart className="w-5 h-5 text-rose-500 mt-1 flex-shrink-0" />
              Remember: Consistency beats perfection. A simple routine you do daily is more effective than an elaborate one you skip.
            </p>
          </div>
        </section>

        {/* Morning Routine */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Sun className="w-8 h-8 text-amber-500" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">
              Morning Routine (5 minutes)
            </h2>
          </div>
          <p className="text-secondary-600 mb-6">
            Morning is about protection—defending your skin from environmental damage and prepping for the day.
          </p>
          <div className="space-y-4">
            {morningRoutine.map((item, index) => (
              <div key={index} className="border-l-4 border-amber-300 pl-5 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-secondary-900">
                    Step {item.step}: {item.product}
                  </h3>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                    {item.duration}
                  </span>
                </div>
                <p className="text-secondary-700 text-sm mb-2">{item.why}</p>
                <p className="text-secondary-600 text-xs bg-gray-50 p-2 rounded">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Evening Routine */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Moon className="w-8 h-8 text-indigo-500" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">
              Evening Routine (7-10 minutes)
            </h2>
          </div>
          <p className="text-secondary-600 mb-6">
            Evening is about repair—removing the day's buildup and giving your skin active ingredients to work with overnight.
          </p>
          <div className="space-y-4">
            {eveningRoutine.map((item, index) => (
              <div key={index} className="border-l-4 border-indigo-300 pl-5 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-secondary-900">
                    Step {item.step}: {item.product}
                  </h3>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                    {item.duration}
                  </span>
                </div>
                <p className="text-secondary-700 text-sm mb-2">{item.why}</p>
                <p className="text-secondary-600 text-xs bg-gray-50 p-2 rounded">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Key Ingredients */}
        <section className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Droplets className="w-8 h-8 text-rose-500" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">
              Key Ingredients to Know
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {keyIngredients.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-bold text-rose-700 mb-1">{item.name}</h3>
                <p className="text-secondary-700 text-sm mb-2">{item.benefit}</p>
                <p className="text-xs text-secondary-500 italic">Note: {item.caution}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            Common Skincare Mistakes to Avoid
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { mistake: "Skipping sunscreen", fix: "This is the #1 anti-aging product. Non-negotiable, even on cloudy days." },
              { mistake: "Using too many actives", fix: "More isn't better. Stick to 1-2 actives, introduce slowly." },
              { mistake: "Expecting overnight results", fix: "Skincare takes 4-6 weeks to show results. Be patient." },
              { mistake: "Forgetting your neck/chest", fix: "These areas age too! Extend all products down to décolletage." },
              { mistake: "Over-exfoliating", fix: "2-3x per week max. Over-exfoliation damages your skin barrier." },
              { mistake: "Not being consistent", fix: "A simple routine done daily beats an elaborate one done sometimes." }
            ].map((item, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-xl">
                <p className="font-bold text-red-600 text-sm mb-1">❌ {item.mistake}</p>
                <p className="text-secondary-700 text-sm">✓ {item.fix}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SkincareGuide;
